import React, { Suspense, ComponentType } from 'react';
import './style.css';
import { CircularProgress } from '@mui/material';

import { tabStorage } from '../../storage/deviceStorage';

const LoadingFallback = (<div className='route-loading'>
  <CircularProgress />
</div>);

const RELOAD_COOLDOWN_MS = 10_000;

const isChunkLoadError = (error: unknown): boolean => {
  const message = (error as { message?: unknown } | null)?.message;
  return (
    typeof message === 'string' &&
    /dynamically imported module|module script failed|importing a module|ChunkLoadError|Loading chunk .* failed|Failed to fetch/i.test(message)
  );
};

const lastReloadAt = (): number => Number(tabStorage.read('chunkReloadAt')) || 0;

const loadWithChunkRecovery = <T,>(load: () => Promise<{ default: T }>): Promise<{ default: T }> =>
  load().catch((error: unknown) => {
    const recentlyReloaded = Date.now() - lastReloadAt() < RELOAD_COOLDOWN_MS;
    if (isChunkLoadError(error) && !recentlyReloaded) {
      tabStorage.write('chunkReloadAt', String(Date.now()));
      window.location.reload();
      return new Promise<{ default: T }>(() => undefined);
    }
    throw error;
  });

function withLazyLoading(importFunc: () => Promise<{ default: ComponentType<any> }>, fallback: React.ReactNode = LoadingFallback) {
  const make = (): React.LazyExoticComponent<ComponentType<any>> =>
    React.lazy(() =>
      loadWithChunkRecovery(importFunc).catch((error: unknown) => {
        current = make();
        throw error;
      }),
    );
  let current = make();

  return (props: any) => {
    const LazyComponent = current;
    return (
      <Suspense fallback={fallback}>
        <LazyComponent {...props} />
      </Suspense>
    );
  };
}

export default withLazyLoading;